import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  fetchPaymentHistory,
  fetchSubscriptionStatus,
} from "../NewResume/paymentAccess";

const SubscriptionContext = createContext(null);
const SUBSCRIPTION_SYNC_EVENT = "resumeiq:subscription-updated";
const SUBSCRIPTION_SYNC_STORAGE_KEY = "resumeiq:subscription-sync:v1";

function getBrowserStorage() {
  if (typeof window === "undefined") return null;

  try {
    return window.localStorage;
  } catch (error) {
    return null;
  }
}

function writeSyncMarker(subscription) {
  const storage = getBrowserStorage();
  if (!storage) return;

  try {
    storage.setItem(
      SUBSCRIPTION_SYNC_STORAGE_KEY,
      JSON.stringify({
        updatedAt: Date.now(),
        subscription: subscription || null,
      }),
    );
  } catch (error) {
    console.error("Unable to persist subscription sync marker.", error);
  }
}

export function SubscriptionProvider({ children }) {
  const [subscription, setSubscription] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const refreshTimerRef = useRef(null);

  const refreshSubscription = useCallback(async (options = {}) => {
    const silent = Boolean(options?.silent);
    if (!silent) {
      setLoading(true);
    }

    try {
      const nextSubscription = await fetchSubscriptionStatus();
      setSubscription(nextSubscription);
      return nextSubscription;
    } catch (error) {
      setSubscription(null);
      return null;
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  const refreshPayments = useCallback(async (options = {}) => {
    const silent = Boolean(options?.silent);
    if (!silent) {
      setLoading(true);
    }

    try {
      const nextPayments = await fetchPaymentHistory();
      setPayments(nextPayments);
      return nextPayments;
    } catch (error) {
      setPayments([]);
      return [];
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, []);

  const refreshAll = useCallback(
    async (options = {}) => {
      const silent = Boolean(options?.silent);
      if (!silent) {
        setLoading(true);
      }

      try {
        const [nextSubscription, nextPayments] = await Promise.all([
          refreshSubscription({ silent: true }),
          refreshPayments({ silent: true }),
        ]);

        return {
          subscription: nextSubscription,
          payments: nextPayments,
        };
      } finally {
        if (!silent) {
          setLoading(false);
        }
      }
    },
    [refreshPayments, refreshSubscription],
  );

  const queueSubscriptionRefresh = useCallback(
    (delayMs = 750) => {
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }

      refreshTimerRef.current = window.setTimeout(() => {
        refreshAll({ silent: true });
      }, delayMs);
    },
    [refreshAll],
  );

  const waitForActiveSubscription = useCallback(
    async ({ initialSubscription = null, timeoutMs = 6000, intervalMs = 300 } = {}) => {
      if (initialSubscription?.active) {
        setSubscription(initialSubscription);
        return initialSubscription;
      }

      const deadline = Date.now() + timeoutMs;
      let latestSubscription = initialSubscription;

      while (Date.now() <= deadline) {
        latestSubscription = await refreshSubscription({ silent: true });

        if (latestSubscription?.active) {
          return latestSubscription;
        }

        await new Promise((resolve) => setTimeout(resolve, intervalMs));
      }

      return latestSubscription;
    },
    [refreshSubscription],
  );

  useEffect(() => {
    const auth = getAuth();
    let cancelled = false;
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setSubscription(null);
        setPayments([]);
        setLoading(false);
        return;
      }

      await refreshAll({ silent: true });
      if (!cancelled) {
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
      unsubscribe();
    };
  }, [refreshAll]);

  useEffect(() => {
    if (typeof window === "undefined") return undefined;

    const handleSubscriptionUpdate = (event) => {
      const nextSubscription = event?.detail?.subscription || null;
      if (nextSubscription) {
        setSubscription(nextSubscription);
      }
      queueSubscriptionRefresh();
    };

    const handleStorageUpdate = (event) => {
      if (event.key !== SUBSCRIPTION_SYNC_STORAGE_KEY) return;

      try {
        const payload = event.newValue ? JSON.parse(event.newValue) : null;
        if (payload?.subscription) {
          setSubscription(payload.subscription);
        }
      } catch (error) {
        // Ignore malformed sync markers and fall back to a backend refresh.
      }

      queueSubscriptionRefresh();
    };

    window.addEventListener(SUBSCRIPTION_SYNC_EVENT, handleSubscriptionUpdate);
    window.addEventListener("storage", handleStorageUpdate);

    return () => {
      window.removeEventListener(SUBSCRIPTION_SYNC_EVENT, handleSubscriptionUpdate);
      window.removeEventListener("storage", handleStorageUpdate);
      if (refreshTimerRef.current) {
        clearTimeout(refreshTimerRef.current);
      }
    };
  }, [queueSubscriptionRefresh]);

  const value = useMemo(
    () => ({
      subscription,
      payments,
      loading,
      active: Boolean(subscription?.active),
      expired: subscription?.status === "expired",
      refresh: refreshSubscription,
      refreshSubscription,
      refreshPayments,
      refreshAll,
      waitForActiveSubscription,
      syncSubscription: (nextSubscription) => {
        if (nextSubscription) {
          setSubscription(nextSubscription);
          writeSyncMarker(nextSubscription);
        }
      },
    }),
    [
      loading,
      payments,
      refreshAll,
      refreshPayments,
      refreshSubscription,
      subscription,
      waitForActiveSubscription,
    ],
  );

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}

export function useSubscriptionContext() {
  return useContext(SubscriptionContext);
}

export default SubscriptionContext;
